import yaml from "js-yaml";
import { marked } from "marked";
import type { ImpactMetric, LighthouseScores, ProjectHighlight } from "./projectImpact";
import { parseLighthouseScores } from "./projectImpact";
import type { ProjectReview } from "./projectStory";
import type { ProjectTechnologyId, ProjectTypeId } from "./projectFilters";
import {
  resolveProjectSegments,
  type ProjectSegmentId,
} from "./projectSegments";


const postModules = import.meta.glob("../../_posts/projects/*.markdown", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export const FEATURED_PROJECT_IDS = ["intobridge", "alloy", "alloy-mobile"];

/**
 * Removed from public portfolio listings (and sitemap/search).
 * Keeps detail routes dead if a stale post file reappears.
 */
export const PORTFOLIO_HIDDEN_IDS = new Set([
  "zlavy110",
  "bobostyling",
  "saris-etiketa",
  "toonect-mobile2",
  "toonect-product",
  "urob",
  "senseilabs",
  "foodtime",
  "leicester",
  "karlasecret",
  "slps",
  "slps-motion",
  "markansys",
  "nescafe",
  "pedro-tip",
  "baximusic",
]);

/** Personal products — Michal’s own ventures (not client work). */
export const OWN_PROJECT_IDS = [
  "map4nomads",
  "superunuseful",
  "superunuseful-app",
  "lececil",
  "mojapolicia",
] as const;

export interface GalleryItem {
  src: string;
  caption: string;
}

export interface Project {
  id: string;
  title: string;
  description: string;
  year?: string;
  client?: string;
  categories: string[];
  types: ProjectTypeId[];
  technologies: ProjectTechnologyId[];
  /** Industry / field (healthcare, finance, automotive, …). */
  segments: ProjectSegmentId[];
  image: string;
  hero: string;
  imgdsc1?: string;
  gallery: GalleryItem[];
  /** Final gallery image shown after main content (original layout: img8). */
  closingImage?: GalleryItem;
  /** Optional YAML: gallery_title overrides default “Web interface” / etc. */
  galleryTitle?: string;
  role?: string;
  video?: string;
  review?: ProjectReview;
  /** Optional YAML: impact: [{ value, label }, ...] */
  impact?: ImpactMetric[];
  /** Optional YAML: lighthouse: { performance, accessibility, best_practices, seo } */
  lighthouse?: LighthouseScores;
  /** Optional YAML: highlight: { value, label } */
  highlight?: ProjectHighlight;
  excerptMarkdown: string;
  bodyMarkdown: string;
  excerptHtml: string;
  contentHtml: string;
  permalink?: string;
  date: Date;
  featured: boolean;
  current: boolean;
  /** Personal product — not client work. */
  own?: boolean;
  /** Legacy work imported from year folders (2013–2017). */
  archive?: boolean;
  /** Shown on experience pages only — hidden from portfolio listings. */
  experienceOnly?: boolean;
}

function slugFromPath(path: string): string {
  const filename = path.split("/").pop() ?? "";
  const match = filename.match(/^\d{4}-\d{2}-\d{2}-(.+)\.markdown$/);
  return match?.[1] ?? filename.replace(/\.markdown$/, "");
}

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith("---")) {
    return { data: {}, content: raw };
  }

  const end = raw.indexOf("\n---", 3);
  if (end === -1) {
    return { data: {}, content: raw };
  }

  const frontmatter = raw.slice(3, end);
  const content = raw.slice(end + 4).replace(/^\r?\n/, "");
  const data = yaml.load(frontmatter) as Record<string, unknown> | null;

  return { data: data ?? {}, content };
}

function getGalleryImage(
  data: Record<string, unknown>,
  index: number,
): GalleryItem | null {
  const src = data[`img${index}`];
  if (typeof src !== "string" || !src) return null;
  const caption = data[`imgdsc${index}`];
  return {
    src,
    caption: typeof caption === "string" ? caption : "",
  };
}

/** Gallery images img2–img7, plus optional extended slides img9–img14 (img8 = closing). */
function getGallery(data: Record<string, unknown>): GalleryItem[] {
  const items: GalleryItem[] = [];
  for (let i = 2; i <= 7; i++) {
    const item = getGalleryImage(data, i);
    if (item) items.push(item);
  }
  for (let i = 9; i <= 14; i++) {
    const item = getGalleryImage(data, i);
    if (item) items.push(item);
  }
  return items;
}

function getClosingImage(data: Record<string, unknown>): GalleryItem | undefined {
  return getGalleryImage(data, 8) ?? undefined;
}

function splitContent(raw: string, separator: string): { excerpt: string; body: string } {
  const index = raw.indexOf(separator);
  if (index === -1) {
    return { excerpt: raw.trim(), body: "" };
  }
  return {
    excerpt: raw.slice(0, index).trim(),
    body: raw.slice(index + separator.length).trim(),
  };
}

function parseMarkdown(md: string): string {
  try {
    return marked.parse(md) as string;
  } catch {
    return "";
  }
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

const TYPE_ALIASES: Record<string, ProjectTypeId> = {
  product: "product-design",
  productdesign: "product-design",
  "product-design": "product-design",
  mobile: "mobile-design",
  webdesign: "web-design",
  "web-design": "web-design",
  motion: "motion-design",
  "motion-design": "motion-design",
  identity: "identity",
};

const VALID_TYPES = new Set<ProjectTypeId>([
  "product-design",
  "mobile-design",
  "web-design",
  "motion-design",
  "identity",
]);

const VALID_TECHNOLOGIES = new Set<ProjectTechnologyId>([
  "wordpress",
  "jamstack",
  "react",
  "vibecoding",
]);

function normalizeType(value: string): ProjectTypeId | null {
  const key = value.toLowerCase().trim();
  const mapped = TYPE_ALIASES[key] ?? key;
  return VALID_TYPES.has(mapped as ProjectTypeId) ? (mapped as ProjectTypeId) : null;
}

function normalizeTechnology(value: string): ProjectTechnologyId | null {
  const key = value.toLowerCase().trim();
  return VALID_TECHNOLOGIES.has(key as ProjectTechnologyId)
    ? (key as ProjectTechnologyId)
    : null;
}

function inferTypesFromPermalink(permalink?: string): ProjectTypeId[] {
  if (!permalink) return [];
  const found: ProjectTypeId[] = [];
  if (permalink.includes("/product-design/")) found.push("product-design");
  if (permalink.includes("/mobile/")) found.push("mobile-design");
  if (permalink.includes("/webdesign/")) found.push("web-design");
  if (permalink.includes("/motion/")) found.push("motion-design");
  if (permalink.includes("/identity/")) found.push("identity");
  return found;
}

function resolveProjectTypes(
  data: Record<string, unknown>,
  categories: string[],
  permalink?: string,
): ProjectTypeId[] {
  const explicit = parseStringArray(data.types)
    .map(normalizeType)
    .filter((t): t is ProjectTypeId => t !== null);

  if (explicit.length > 0) return [...new Set(explicit)];

  const inferred = new Set<ProjectTypeId>();

  for (const cat of categories) {
    const type = normalizeType(cat);
    if (type) inferred.add(type);
  }

  const tags = parseStringArray(data.tags);
  for (const tag of tags) {
    const type = normalizeType(tag);
    if (type) inferred.add(type);
  }

  for (const type of inferTypesFromPermalink(permalink)) {
    inferred.add(type);
  }

  return [...inferred];
}

function resolveProjectTechnologies(data: Record<string, unknown>): ProjectTechnologyId[] {
  return [
    ...new Set(
      parseStringArray(data.technologies)
        .map(normalizeTechnology)
        .filter((t): t is ProjectTechnologyId => t !== null),
    ),
  ];
}

function parseDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return new Date(0);
}

function parseImpactMetrics(data: Record<string, unknown>): ImpactMetric[] | undefined {
  const raw = data.impact;
  if (!Array.isArray(raw)) return undefined;
  const metrics: ImpactMetric[] = [];
  for (const item of raw) {
    if (item && typeof item === "object" && "value" in item && "label" in item) {
      const row = item as Record<string, unknown>;
      if (typeof row.value === "string" && typeof row.label === "string") {
        metrics.push({ value: row.value, label: row.label });
      }
    }
  }
  return metrics.length > 0 ? metrics : undefined;
}

function parseHighlight(data: Record<string, unknown>): ProjectHighlight | undefined {
  const raw = data.highlight;
  if (!raw || typeof raw !== "object" || !("value" in raw) || !("label" in raw)) {
    return undefined;
  }
  const row = raw as Record<string, unknown>;
  if (typeof row.value === "string" && typeof row.label === "string") {
    return { value: row.value, label: row.label };
  }
  return undefined;
}

function parseReviewFromMarkdown(body: string): ProjectReview | undefined {
  const match = body.match(/^###\s+Review\s*\n+([\s\S]*?)(?=^###\s+|\s*$)/im);
  if (!match) return undefined;

  const block = match[1].trim();
  const quoteMatch = block.match(/^>\s?(.+)$/m);
  const quote = quoteMatch
    ? quoteMatch[1].trim()
    : block.replace(/^>+\s?/gm, "").split("\n")[0]?.trim() ?? "";
  const author = block
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith(">"))
    .join(" ")
    .trim();

  if (!quote) return undefined;
  return { quote, author: author || "Client" };
}

function parseMarkdownFile(path: string, raw: string): Project {
  const { data, content } = parseFrontmatter(raw);
  const id = slugFromPath(path);
  const excerptSeparator =
    typeof data.excerpt_separator === "string" ? data.excerpt_separator : "<!--more-->";
  const { excerpt, body } = splitContent(content, excerptSeparator);

  const title = typeof data.title === "string" ? data.title : id;
  const description =
    typeof data.description === "string" ? data.description : stripHtml(title);

  const categories = Array.isArray(data.categories)
    ? data.categories.filter((c): c is string => typeof c === "string")
    : [];

  const image = typeof data.img === "string" ? data.img : "";
  const hero = typeof data.hero === "string" ? data.hero : image;
  const permalink = typeof data.permalink === "string" ? data.permalink : undefined;
  const types = resolveProjectTypes(data, categories, permalink);
  const technologies = resolveProjectTechnologies(data);
  const segments = resolveProjectSegments(data, id, body);

  return {
    id,
    title,
    description,
    year: typeof data.year === "string" ? data.year : undefined,
    client: typeof data.client === "string" ? data.client : undefined,
    categories,
    types,
    technologies,
    segments,
    image,
    hero,
    imgdsc1: typeof data.imgdsc1 === "string" ? data.imgdsc1 : undefined,
    gallery: getGallery(data),
    closingImage: getClosingImage(data),
    galleryTitle: typeof data.gallery_title === "string" ? data.gallery_title : undefined,
    role: typeof data.role === "string" ? data.role : "Lead Product Designer",
    video: typeof data.video === "string" ? data.video : undefined,
    review: parseReviewFromMarkdown(body),
    impact: parseImpactMetrics(data),
    lighthouse: parseLighthouseScores(data.lighthouse),
    highlight: parseHighlight(data),
    excerptMarkdown: excerpt,
    bodyMarkdown: body,
    excerptHtml: parseMarkdown(excerpt),
    contentHtml: parseMarkdown(body),
    permalink,
    date: parseDate(data.date),
    featured: FEATURED_PROJECT_IDS.includes(id),
    current: categories.includes("current") || categories.includes("2026"),
    own:
      data.own === true ||
      categories.includes("own") ||
      (OWN_PROJECT_IDS as readonly string[]).includes(id),
    archive: data.archive === true || categories.includes("archive"),
    experienceOnly: data.experienceOnly === true || categories.includes("experience-only"),
  };
}

export function getOwnProjects(): Project[] {
  return allProjects.filter((p) => p.own);
}

const allProjects: Project[] = Object.entries(postModules)
  .map(([path, raw]) => parseMarkdownFile(path, raw))
  .sort((a, b) => b.date.getTime() - a.date.getTime());

export function getProjects(): Project[] {
  return allProjects;
}

export function getPortfolioProjects(): Project[] {
  return allProjects.filter((p) => !p.experienceOnly && !PORTFOLIO_HIDDEN_IDS.has(p.id));
}

export function getCurrentProjects(): Project[] {
  return allProjects.filter((p) => p.current);
}

export function getPastProjects(): Project[] {
  return allProjects.filter((p) => !p.current);
}

export function getFeaturedProjects(): Project[] {
  return FEATURED_PROJECT_IDS.map((id) => getProjectById(id)).filter(
    (p): p is Project => p !== undefined,
  );
}

export function getProjectById(id: string): Project | undefined {
  return allProjects.find((p) => p.id === id);
}

const PROJECT_CARD_IMAGE_LIMIT = 3;

/** Cover + gallery images for portfolio card slideshow (max 3, no duplicates). */
export function getProjectCardImages(project: Project, limit = PROJECT_CARD_IMAGE_LIMIT): string[] {
  const images: string[] = [];
  const add = (src: string) => {
    if (!src || images.includes(src) || images.length >= limit) return;
    images.push(src);
  };

  add(project.image);
  if (project.hero && project.hero !== project.image) {
    add(project.hero);
  }
  for (const item of project.gallery) {
    add(item.src);
  }

  return images;
}
